import { type IndexableTypePart, type Table } from "dexie";
import NotFoundException from "../../Routes/SellScreen/Exceptions/Data/NotFoundException";
import BaseRepository from "../_Bases/BaseRepository";

export default abstract class BaseIndexedDBRepository<
    DataType extends object,
    IdType extends IndexableTypePart = string
> extends BaseRepository<DataType, IdType> {
    public readonly source: Table<DataType, IdType>;
    protected readonly abstract dataTypeName: string;

    public get(id: IdType): Promise<DataType> {
        return this.source
            .get(id)
            .then(value => {
                if(!value) {
                    throw new NotFoundException(`${this.dataTypeName} Not Found (${id})`);
                }

                return value;
            });
    }

    public put(item: Array<DataType> | DataType): Promise<IdType> {
        if(Array.isArray(item)) {
            return this.source.bulkPut(item);
        }

        return this.source.put(item);
    }

    public update(id: IdType, changes: Partial<DataType>): Promise<void> {
        return this.source.update(id, changes)
            .then(() => {
                // To return void
            });
    }

    public all(orderBy?: string): Promise<Array<DataType>> {
        if(orderBy) {
            return this.source.orderBy(orderBy).toArray();
        }

        return this.source.toArray();
    }

    public count(): Promise<number> {
        return this.source.count();
    }

    public delete(items: ReadonlyArray<IdType> | IdType): Promise<void> {
        if(Array.isArray(items)) {
            if(items.length === 0) {
                return Promise.resolve();
            }

            return this.source.bulkDelete(items as any);
        } else if(typeof items === "string" && items === "") {
            return Promise.resolve();
        }

        return this.source.delete(items as IdType);
    }

    public empty(): Promise<void> {
        return this.source.clear();
    }

    public raw(): any {
        return this.source;
    }

    public each(callback: (item: DataType) => void): Promise<void> {
        // Note: This is fundamentally slower than retrieving all of the objects at once
        // but is better for memory usage as it only retrieves one at a time
        return this.source.each(callback);
    }
}
